import uniqBy from 'lodash/uniqBy';
import { FormTranslatorPayload, FormTranslatorResponse, ICRMFormTranslator } from '../../CRMHandler';
import {
  VEEVA_VAULT_TABLES,
  VeevaVaultFieldRelationshipType,
  VeevaVaultFieldStatus,
  VeevaVaultFormSetting,
  VeevaVaultItemType,
  VeevaVaultObjectField,
  VeevaVaultObjectFieldType,
  VeevaVaultObjectLayoutSection,
  VeevaVaultSectionLayoutItem,
  VeevaVaultSectionLayoutType,
  VeevaVaultSectionType,
} from '../../CRMVeevaVaultTypes';
import {
  ControlType,
  CustomFieldDefinition,
  CustomFieldUsage,
  CustomFieldValueDefinition,
  FieldDataType,
  FieldStatus,
  FieldPosition,
} from '@alucio/aws-beacon-amplify/src/models';
import logger from 'src/utils/logger';
import {
  getFormattedDependentPicklistValueDefinitions,
  getPartialCustomField,
  getPartialCustomFieldValueDefinitions,
  getSectionLabel,
  resolveProductMapPicklist,
  shouldSectionBeIgnored,
} from './VeevaVaultTranslatorUtils';

import { getDefaultCustomFieldValuesMap } from 'src/components/CustomFields/CustomFieldValueUtilities';
import {
  handleVeevaVaultItemControlType,
  handleVeevaVaultSectionControlType,
  OMIT_FIELD_POSITION_SETTING,
  VEEVA_SPECIAL_ITEM_HANDLER,
} from './VeevaVaultControlHandler';

const FIELDS_TO_SKIP: { [tableName: string]: string[] } = {
  [VEEVA_VAULT_TABLES.CALL]: ['account__v', 'address__v', 'call_datetime__v', 'ownerid__v'],
  [VEEVA_VAULT_TABLES.MEDICAL_DISCUSSION]: ['interaction__v'],
  [VEEVA_VAULT_TABLES.CALL_DETAIL]: ['call2__v'],
  [VEEVA_VAULT_TABLES.CALL_DISCUSSION]: ['call2__v', 'status__v', 'product_map__v'],
}

const VEEVA_VAULT_FIELD_TYPE_MAP = {
  [VeevaVaultObjectFieldType.STRING]: {
    fieldType: FieldDataType.STRING,
    controlType: ControlType.INPUT,
  },
  [VeevaVaultObjectFieldType.RICH_TEXT]: {
    fieldType: FieldDataType.STRING,
    controlType: ControlType.TEXTAREA,
  },
  [VeevaVaultObjectFieldType.LONG_TEXT]: {
    fieldType: FieldDataType.STRING,
    controlType: ControlType.TEXTAREA,
  },
  [VeevaVaultObjectFieldType.DATETIME]: {
    fieldType: FieldDataType.DATE,
    controlType: ControlType.DATETIMEPICKER,
  },
  [VeevaVaultObjectFieldType.PICKLIST]: {
    fieldType: FieldDataType.CATEGORICAL,
    controlType: ControlType.SELECT,
  },
  [VeevaVaultObjectFieldType.OBJECT]: {
    fieldType: FieldDataType.CATEGORICAL,
    controlType: ControlType.SELECT,
  },
  [VeevaVaultObjectFieldType.NUMBER]: {
    fieldType: FieldDataType.NUMBER,
    controlType: ControlType.INPUT,
  },
  [VeevaVaultObjectFieldType.DATE]: {
    fieldType: FieldDataType.DATE,
    controlType: ControlType.DATEPICKER,
  },
  [VeevaVaultObjectFieldType.BOOLEAN]: {
    fieldType: FieldDataType.CATEGORICAL,
    controlType: ControlType.CHECKBOX,
    fieldValueDefinitions: [{
      createdAt: new Date().toISOString(),
      dependentValueIds: [],
      id: 'true',
      value: 'YES',
      label: '',
    },
    {
      createdAt: new Date().toISOString(),
      dependentValueIds: [],
      id: 'false',
      value: 'NO',
      label: '',
    }],
  },
};

export class VeevaVaultFormTranslator implements ICRMFormTranslator {
  private formSettings: VeevaVaultFormSetting[] = [];
  private mainTable: string;

  constructor(formTranslatorPayload: FormTranslatorPayload, mainTable: string) {
    if (!mainTable) {
      throw Error('Invalid mainTable configuration');
    }

    this.formSettings = formTranslatorPayload.veevaVaultFormSettings;
    this.mainTable = mainTable || '';
  }

  getFormCustomFieldConfig(): FormTranslatorResponse {
    const crmDefaultValues = {};
    const crmCustomFields: CustomFieldDefinition[] = [];
    const mainFormSetting = this.formSettings.find(setting => setting.id === this.mainTable);
    const resolvedSetting = mainFormSetting || this.formSettings.find(setting => setting.apiName === this.mainTable);

    if (!resolvedSetting) {
      logger.veevaVaultTranslator.warn(`No VeevaVaultFormSetting found for main table [${this.mainTable}]`);
      return {
        crmCustomFields,
        crmDefaultValues,
      };
    }

    const { layout } = resolvedSetting;
    if (!layout) {
      logger.veevaVaultTranslator.warn(`No layout found for main table [${this.mainTable}]`);
      return {
        crmCustomFields,
        crmDefaultValues,
      };
    }

    layout.sections.forEach((section) => {
      logger.veevaVaultTranslator.info('Processing section: ', section.name);
      crmCustomFields.push(...this.processSection(section, resolvedSetting));
    });

    const uniqueCustomFields = uniqBy(crmCustomFields, 'id');
    return {
      crmCustomFields: uniqueCustomFields,
      crmDefaultValues: getDefaultCustomFieldValuesMap(uniqueCustomFields),
    };
  }

  private processSection(
    section: VeevaVaultObjectLayoutSection,
    veevaVaultFormSetting: VeevaVaultFormSetting,
  ): CustomFieldDefinition[] {
    if (shouldSectionBeIgnored(section, veevaVaultFormSetting)) {
      return [];
    }

    const customFields: CustomFieldDefinition[] = [];

    if (section.type === VeevaVaultSectionType.DETAIL) {
      const items = section.properties?.items || [];
      let sectionCustomFields: CustomFieldDefinition[] = [];

      // 1. PROCESSES THE ITEMS INTO CUSTOM FIELDS //
      items.forEach((item) => {
        if (!FIELDS_TO_SKIP[veevaVaultFormSetting.apiName]?.includes(item.reference || '')) {
          logger.veevaVaultTranslator.info('Processing item: ', item.reference || item.type);
          sectionCustomFields.push(...this.processItem(item, veevaVaultFormSetting));
        }
      });

      // 2. ORDERS FIELDS IN TWO COLUMNS IF REQUIRED //
      if (section.properties.layout_type === VeevaVaultSectionLayoutType.TWO_COLUMNS) {
        sectionCustomFields = sectionCustomFields.map((field, index) => {
          const shouldOmmitPossition =
            OMIT_FIELD_POSITION_SETTING[veevaVaultFormSetting.apiName]?.includes(field.fieldName);

          if (shouldOmmitPossition) {
            return field;
          }

          return {
            ...field,
            settings: {
              fieldPosition: field.settings?.fieldPosition ||
                (index % 2 !== 0 ? FieldPosition.RIGHT : FieldPosition.LEFT),
            },
          };
        });
      }
      customFields.push(...sectionCustomFields);
    } else if (section.type === VeevaVaultSectionType.CONTROL) {
      logger.veevaVaultTranslator.info('Processing control section: ', section.name);
      const controlCustomFields =
        handleVeevaVaultSectionControlType(
          section,
          this.formSettings,
          this.processChild.bind(this),
          veevaVaultFormSetting);
      customFields.push(...controlCustomFields);
    }

    // 3. ADDS SECTION'S LABEL //
    if (section.title && customFields.length) {
      customFields.unshift(getSectionLabel(section));
    }
    return customFields;
  }

  private processChild(veevaVaultFormSetting: VeevaVaultFormSetting): CustomFieldDefinition[] {
    logger.veevaVaultTranslator.info(`Processing child form setting for object [${veevaVaultFormSetting.apiName}]...`);
    const { objectInfo, layout } = veevaVaultFormSetting;
    const customFields: CustomFieldDefinition[] = [];

    if (!objectInfo || !layout) {
      logger.veevaVaultTranslator.warn(`Missing objectInfo or layout for [${veevaVaultFormSetting.apiName}]...`);
      return [];
    }

    layout.sections.forEach((section) => {
      logger.veevaVaultTranslator.info('Processing section: ', section.name);
      const plainCustomFields = this.processSection(section, veevaVaultFormSetting);
      const formattedCustomFields = plainCustomFields.map((field) => ({
        ...field,
        id: `${veevaVaultFormSetting.id}-FIELD_NAME-${field.id}`,
        isChildField: true,
      }));
      customFields.push(...formattedCustomFields);
    });

    const childrenFieldIds = customFields.map(field => field.id);

    const customFieldObject: CustomFieldDefinition = {
      id: veevaVaultFormSetting.id,
      fieldType: FieldDataType.OBJECT,
      controlType: ControlType.OBJECT,
      fieldName: objectInfo.name,
      fieldLabel: veevaVaultFormSetting.label,
      fieldValueDefinitions: [],
      createdAt: new Date().toISOString(),
      status: FieldStatus.ENABLED,
      usage: [CustomFieldUsage.MEETING],
      objectSetting: {
        childrenFieldIds,
        restriction: {
          syncDelete: !veevaVaultFormSetting.layout.permissions?.delete,
          syncUpdate: !veevaVaultFormSetting.layout.permissions?.edit,
        },
      },
    };

    if (veevaVaultFormSetting.apiName === VEEVA_VAULT_TABLES.CALL_DETAIL) {
      const product = customFields.find(field => field.fieldName === 'product__v');
      const needsOtherInput = customFields.some(field => field.id !== product?.id && field.required &&
        !field.fieldValueDefinitions.some(value => value.isDefault));
      if (product?.fieldValueDefinitions.length && !needsOtherInput) {
        return [...customFields, {
          ...customFieldObject,
          controlType: ControlType.SELECT,
          fieldLabel: '',
          fieldValueDefinitions: product.fieldValueDefinitions,
          objectSetting: {
            ...customFieldObject.objectSetting,
            childrenFieldIds: [product.id, ...childrenFieldIds.filter(id => id !== product.id)],
          },
        }];
      }
    }

    customFields.push(customFieldObject);
    return customFields;
  }

  private processItem(
    item: VeevaVaultSectionLayoutItem,
    veevaVaultFormSetting: VeevaVaultFormSetting,
  ): CustomFieldDefinition[] {
    if (item.type === VeevaVaultItemType.CONTROL) {
      logger.veevaVaultTranslator.info('Processing control item: ', item.reference);
      return handleVeevaVaultItemControlType(item);
    }

    if (item.status !== VeevaVaultFieldStatus.ACTIVE) {
      logger.veevaVaultTranslator.warn(`Field [${item.reference}] is not active, skipping...`)
      return [];
    }

    const field = veevaVaultFormSetting.objectInfo?.fields
      ?.find(({ name }) => name === item.reference);

    if (!field) {
      logger.veevaVaultTranslator.warn(
        `Field [${item.reference}] not found in objectInfo for [${veevaVaultFormSetting.apiName}]...`,
      );
      return [];
    } else if (!field.editable) {
      logger.veevaVaultTranslator.warn(`Skipping non-editable field [${field.name}]`);
      return [];
    }

    const specialFieldHandler = VEEVA_SPECIAL_ITEM_HANDLER[veevaVaultFormSetting.apiName]?.[field?.name || ''];
    if (specialFieldHandler) {
      logger.veevaVaultTranslator.info('Processing special item: ', field?.name);
      return specialFieldHandler(item, field);
    }

    logger.veevaVaultTranslator.info(
      `Processing item [${item.reference}] of type [${field.type}]...`,
    );

    if (this.isLookupField(field)) {
      return this.handleLookupItem(item, field, veevaVaultFormSetting);
    }

    if (field.type === VeevaVaultObjectFieldType.PICKLIST) {
      return this.handlePicklistItem(item, field, veevaVaultFormSetting);
    }

    return this.handleStandardField(item, field);
  }

  private isLookupField(field: VeevaVaultObjectField): boolean {
    return (
      field.type === VeevaVaultObjectFieldType.OBJECT &&
      field.relationship_type === VeevaVaultFieldRelationshipType.REFERENCE
    );
  }

  private handleStandardField(
    item: VeevaVaultSectionLayoutItem,
    field: VeevaVaultObjectField,
  ): CustomFieldDefinition[] {
    const fieldTypeMapping = { ...VEEVA_VAULT_FIELD_TYPE_MAP[field.type] };

    if (!fieldTypeMapping) {
      logger.veevaVaultTranslator.warn(`Unsupported field type: [${field.type}] for field [${field.name}]`);
      return [];
    }

    if (item.read_only) {
      if (field.default_value) {
        fieldTypeMapping.controlType = ControlType.INFO_MESSAGE;
      } else {
        logger.veevaVaultTranslator.warn(
          `The field [${field.name}] is read only and does not have a default value, skipping...`);
        return [];
      }
    }

    return [{
      ...getPartialCustomField(field),
      ...fieldTypeMapping,
      required: item.required || false,
      description: field.help_content || undefined,
    }];
  }

  private handleLookupItem(
    item: VeevaVaultSectionLayoutItem,
    field: VeevaVaultObjectField,
    veevaVaultFormSetting: VeevaVaultFormSetting,
  ): CustomFieldDefinition[] {
    const lookupRecords = veevaVaultFormSetting.lookups?.[field.name];

    if (!lookupRecords || !Array.isArray(lookupRecords) || lookupRecords.length === 0) {
      logger.veevaVaultTranslator.warn(
        `Lookup data not found for field [${field.name}] in ${veevaVaultFormSetting.apiName}`,
      );
      return [];
    }

    const fieldValueDefinitions: CustomFieldValueDefinition[] = lookupRecords.map((record) => ({
      ...getPartialCustomFieldValueDefinitions(),
      id: record.id,
      value: record.id,
      label: record.name__v || record.id,
    }));

    return [{
      ...getPartialCustomField(field),
      ...VEEVA_VAULT_FIELD_TYPE_MAP[VeevaVaultObjectFieldType.OBJECT]!,
      required: item.required,
      description: field.help_content || undefined,
      fieldValueDefinitions,
    }];
  }

  private handlePicklistItem(
    item: VeevaVaultSectionLayoutItem,
    field: VeevaVaultObjectField,
    veevaVaultFormSetting: VeevaVaultFormSetting,
  ): CustomFieldDefinition[] {
    const fieldPicklist = veevaVaultFormSetting.picklists?.[field.name];

    if (!fieldPicklist) {
      logger.veevaVaultTranslator.warn(
        `Picklist data not found for field [${field.name}] in [${veevaVaultFormSetting.apiName}]`,
      );
      return [];
    }

    const picklistData = resolveProductMapPicklist(fieldPicklist, veevaVaultFormSetting);
    let dependentFieldId: string | undefined;
    const fieldValueDefinitions: CustomFieldValueDefinition[] = [];
    logger.veevaVaultTranslator.info('Handling picklist...', { field, picklistData });

    if (picklistData.controllingPicklistName && picklistData.picklistDependencies) {
      if (!veevaVaultFormSetting.isMainTable) {
        dependentFieldId = `${veevaVaultFormSetting.id}-FIELD_NAME-${picklistData.controllingPicklistName}`;
      } else {
        dependentFieldId = picklistData.controllingPicklistName;
      }

      fieldValueDefinitions.push(...getFormattedDependentPicklistValueDefinitions(picklistData));
    } else {
      picklistData.picklistValues?.forEach((picklistValue) => {
        fieldValueDefinitions.push({
          ...getPartialCustomFieldValueDefinitions(),
          id: picklistValue.name,
          value: picklistValue.name,
          label: picklistValue.label,
        });
      });
    }

    return [{
      ...getPartialCustomField(field),
      ...VEEVA_VAULT_FIELD_TYPE_MAP[VeevaVaultObjectFieldType.PICKLIST]!,
      fieldType: field.multi_value ? FieldDataType.MULTICATEGORICAL : FieldDataType.CATEGORICAL,
      required: item.required,
      description: field.help_content || undefined,
      fieldValueDefinitions,
      dependentFieldId,
    }];
  }
}
